import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from 'reactstrap';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import IntlMessages from '../../helpers/IntlMessages';

import firebaseApp from '../../services/Firebase';
import { trackPropertiesSegment } from '../../utils/segment';
import { useIntl } from 'react-intl';
import useDataArchive from '../../hooks/useDataArchive';

export default function TagModal(props) {
  const { messages } = useIntl();
  const { user } = useSelector(({ auth }) => auth);
  const { deleteDataFromArchive } = useDataArchive();

  // States
  const [tag, setTag] = useState({});
  const [isLoading, setLoading] = useState(true);

  // Effects
  useEffect(() => {
    (async () => {
      if (props.id !== 'new') await loadTag();
      setLoading(false);
    })();
  }, []);

  // Functions
  const loadTag = async () => {
    const tagSnap = await firebaseApp
      .getTagFromId(user.organizationId, props.id)
      .get();

    setTag(tagSnap.data());
    return;
  };

  // Update User
  const updateTag = async (values, setSubmitting) => {
    if (props.id !== 'new') {
      await firebaseApp.updateTagFromOrganization(
        user.organizationId,
        props.id,
        values
      );

      trackPropertiesSegment('Tag updated', {
        user,
        tag: values,
      });
    } else {
      await firebaseApp.createTagFromOrganization(user.organizationId, values);

      trackPropertiesSegment('New tag created', {
        user,
        tag: values,
      });
    }

    setSubmitting(false);

    deleteDataFromArchive('tags');

    props.toggleModal();
  };

  // Render
  if (isLoading) {
    return (
      <Modal className="admin" isOpen={true} toggle={props.toggleModal}>
        <ModalBody className="edit-group">
          <div className="loading" />
        </ModalBody>
      </Modal>
    );
  }

  // Render
  return (
    <Modal className="admin" isOpen={true} toggle={props.toggleModal}>
      <ModalHeader toggle={props.toggleModal}>
        <IntlMessages id="admin.policy.tag.Modal.Add" />
      </ModalHeader>
      <Formik
        initialValues={{
          name: tag.name || '',
          externalId: tag.externalId || '',
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required(
            messages['forms.validation.firstName.required']
          ),
          externalId: Yup.string(),
        })}
        onSubmit={(values, { setSubmitting }) => {
          updateTag(values, setSubmitting);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <ModalBody>
              <Row className="mb-1">
                <Label className="form-group has-top-label mb-0 w-100">
                  <Field type="input" name="name" className="form-control" />
                  <IntlMessages id="admin.policy.motive.Name" />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </Label>
              </Row>
              <Row className="mb-1">
                <Label className="form-group has-top-label mb-0 w-100">
                  <Field
                    type="input"
                    name="externalId"
                    className="form-control"
                  />
                  <IntlMessages id="admin.policy.motive.ExternalId" />
                  <ErrorMessage
                    name="externalId"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </Label>
              </Row>
            </ModalBody>
            <ModalFooter>
              <div className="d-flex justify-content-between align-items-center">
                <Button
                  type="submit"
                  color="primary"
                  outline
                  size="lg"
                  disabled={isSubmitting}
                >
                  <IntlMessages id="forms.action.save" />
                </Button>
              </div>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
